// src/ReportPage.js
import React, { Component, useState, useMemo } from "react";
import {
  Box,
  Stack,
  Paper,
  Button,
  CircularProgress,
  Typography,
  Autocomplete,
  TextField,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Popover,
  ButtonBase,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ConstituencyReportView from "./ConstituencyReportView";
import BusinessReportView from "./BusinessReportView";
import HolidayTaxReportView from "./HolidayTaxReportView";
import MotabilityReportView from "./MotabilityReportView";
import WaspiReportView from "./WaspiReportView";
import McCainReportView from "./McCainReportView";
import BuildYourOwnView, { BUILD_BLOCK_TYPES, WIDTH_PRESETS, GRID_COLS, MAX_ROWS, defaultRowSpan } from "./BuildYourOwnView";
import QuestionDrilldownView from "./QuestionDrilldownView";
import { REPORT_TEMPLATES } from "./ReportTemplates";
import { HOLIDAY_TAX_CLIENTS, MOTABILITY_CLIENTS, WASPI_CLIENTS, MCCAIN_CLIENTS } from "./utils/polAggregators";
import { segmentOptionsFor, MCCAIN_VI_COLORS, MCCAIN_REPORT_VI_CODES, readableOn } from "./utils/segmentConfig";
import { schemaRequestFields } from "./utils/schemaTypes";
import { weightedMeanByKey } from "./utils/keyWeights";
import { tableFromIPC } from "apache-arrow";

// Shared "nice" dropdown menu styling (rounded, subtle border + shadow) so
// MUI selects don't use the plain default popover.
const NICE_MENU_PROPS = {
  MenuProps: {
    PaperProps: {
      sx: {
        mt: 0.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 10px 28px rgba(15,23,42,0.14)",
        "& .MuiMenuItem-root": { fontSize: 13, py: 0.75 },
      },
    },
  },
};

// ─── helpers ────────────────────────────────────────────────────────────────

function rowsFromTable(table) {
  return table.toArray().map(row => row.toJSON());
}

async function waitForImages(rootEl) {
  const imgs = Array.from(rootEl.querySelectorAll("img"));
  await Promise.all(
    imgs.map(img => {
      if (img.complete && img.naturalWidth > 0) return;
      return new Promise(res => {
        img.addEventListener("load", res, { once: true });
        img.addEventListener("error", res, { once: true });
      });
    })
  );
}

// Per-type visual config used in both the selector cards and the queue pills
const REPORT_TYPE_CONFIG = {
  default: {
    icon: "🗳",
    title: "Political",
    desc: "MP, majority & voter profile",
    dot: "#378add",
    pillBg: "#e6f1fb",
    pillText: "#185fa5",
    iconBg: "#e6f1fb",
    comingSoon: false,
  },
  business: {
    icon: "📊",
    title: "Business",
    desc: "Economy & industry data",
    dot: "#639922",
    pillBg: "#eaf3de",
    pillText: "#3b6d11",
    iconBg: "#eaf3de",
    comingSoon: false,
  },
  client: {
    icon: "🏖",
    title: "Holiday Tax",
    desc: "Tourist tax constituency report",
    dot: "#b45309",
    pillBg: "#fef3c7",
    pillText: "#92400e",
    iconBg: "#fef3c7",
    comingSoon: false,
  },
  motability: {
    icon: "♿",
    title: "Motability",
    desc: "Motability Scheme constituency report",
    dot: "#003087",
    pillBg: "#e6eaf5",
    pillText: "#003087",
    iconBg: "#e6eaf5",
    comingSoon: false,
  },
  mccain: {
    icon: "🌾",
    title: "McCain",
    desc: "Farming & food policy, by voting intention",
    dot: "#c8102e",
    pillBg: "#fdeaee",
    pillText: "#9b0c23",
    iconBg: "#fdeaee",
    comingSoon: false,
  },
  waspi: {
    icon: "⚖",
    title: "WASPI",
    desc: "WASPI campaign constituency report",
    dot: "#6d28d9",
    pillBg: "#ede9fe",
    pillText: "#5b21b6",
    iconBg: "#ede9fe",
    comingSoon: false,
  },
};
// Custom-report builder options. Shown when the studio is in "custom" mode
// (toggled via the pencil button in the header). The default studio experience
// remains the pre-made templates in REPORT_TYPE_CONFIG above - custom is opt-in.
//   extend    → Type 1: adapt an existing template (notes box, swap the highlight)
//   drilldown → Type 2: deep-dive a single question with top-audience breakdowns
//   build     → Type 3: full drag-and-drop builder (coming soon)
const CUSTOM_REPORT_TYPES = {
  extend: {
    icon: "✦",
    title: "Adapt an existing template",
    desc: "Change the emphasis of a standard report",
    comingSoon: false,
  },
  drilldown: {
    icon: "🔍",
    title: "Question drill-down",
    desc: "Deep-dive one question, with audience breakdowns",
    comingSoon: false,
  },
  build: {
    icon: "🧩",
    title: "Build your own",
    desc: "Drag & drop questions and pick your own charts",
    comingSoon: false,
  },
};

// Questions that can be promoted to the political report's highlight slot (1B).
const POLITICAL_HERO_OPTIONS = [
  { id: "vote_intention",  label: "Vote intention (default)" },
  { id: "national_issues", label: "Most important issues" },
  { id: "priorities",      label: "Local priorities" },
  { id: "best_pm",         label: "Best prime minister" },
];

// Voting-intention cuts offered for the McCain report: the unfiltered report,
// then each party. Labels come from SEGMENT_CONFIGS.mccain rather than being
// restated, so the codes stay defined in one place; MCCAIN_REPORT_VI_CODES
// narrows that full 11-code filter list to the parties worth a client report.
const MCCAIN_VI_OPTIONS = [
  { code: "all", label: "All voters", color: "#64748b" },
  ...segmentOptionsFor("mccain")
    .filter(([code]) => MCCAIN_REPORT_VI_CODES.includes(code))
    .map(([code, label]) => ({
      code,
      label,
      color: MCCAIN_VI_COLORS[code] || "#64748b",
    })),
];

const DEMO_LIFETIME_LIMIT = 2;
const DEMO_QUEUE_KEY = "demo_report_queue_v1";
const DEMO_USED_KEY = "demo_report_used_v1";

// Step label used in the sidebar
function StepLabel({ number, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 16, height: 16, borderRadius: "50%",
          bgcolor: "#111827", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, fontWeight: 500, flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography
        variant="overline"
        sx={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.08em", color: "text.secondary" }}
      >
        {label}
      </Typography>
    </Stack>
  );
}


// Analyst notes / executive summary block appended to an extended report.
// Rendered as a sibling of the base report view inside the same export
// container, so it works for any base template and is captured by html2canvas
// (and paginated by generatePdfFor) without touching the report views.
function NotesBlock({ text, position = "bottom" }) {
  if (!text) return null;
  const title = position === "top" ? "Executive summary" : "Analyst notes & recommendations";
  return (
    <Box
      sx={{
        width: 794,
        mx: "auto",
        my: 1.5,
        p: "16px 20px",
        bgcolor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderLeft: "4px solid #0abfbc",
        borderRadius: "10px",
        boxSizing: "border-box",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#0abfbc",
          mb: "6px",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: 1.5,
          color: "#0f172a",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

// Two-level question picker: a select-style field that opens a popover of
// category accordions - you see the section titles first, then expand one to
// choose a question underneath. Likert-grid questions nest one level deeper as
// their own sub-dropdown (grid overview + each sub-question). A search box
// filters across everything (flat `options`).
function QuestionPicker({ options, tree, value, onChange, placeholder = "Select a question" }) {
  const [anchor, setAnchor] = useState(null);
  const [openCat, setOpenCat] = useState(null);
  const [openGrid, setOpenGrid] = useState(null);
  const [query, setQuery] = useState("");

  const selected = (options || []).find((o) => o.id === value) || null;

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return null;
    return (options || []).filter(
      (o) => o.label.toLowerCase().includes(q) || (o.category || "").toLowerCase().includes(q)
    );
  }, [q, options]);

  const open = Boolean(anchor);
  const close = () => { setAnchor(null); setQuery(""); };
  const select = (id) => { onChange(id); close(); };
  const pickCat = (cat) => setOpenCat((prev) => (prev === cat ? null : cat));

  return (
    <>
      <ButtonBase
        onClick={(e) => { setAnchor(e.currentTarget); setOpenCat(selected?.category || null); }}
        sx={{
          width: "100%", justifyContent: "space-between",
          border: "1px solid", borderColor: open ? "primary.main" : "divider",
          borderRadius: 1, px: 1.25, py: 1, textAlign: "left",
          bgcolor: "background.paper",
          "&:hover": { borderColor: open ? "primary.main" : "text.secondary" },
        }}
      >
        <Typography sx={{ fontSize: 13, color: selected ? "text.primary" : "text.disabled", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: anchor ? anchor.offsetWidth : undefined,
              maxHeight: 360,
              mt: 0.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 10px 28px rgba(15,23,42,0.14)",
            },
          },
        }}
      >
        {/* Search box */}
        <Box sx={{
          position: "sticky", top: 0, zIndex: 1,
          p: 1, bgcolor: "background.paper",
          borderBottom: "1px solid", borderColor: "divider",
        }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search questions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Box>

        {matches ? (
          /* Flat, filtered results while searching */
          matches.length ? (
            matches.map((o) => (
              <MenuItem
                key={o.id}
                selected={o.id === value}
                onClick={() => select(o.id)}
                sx={{ display: "block", width: "100%", textAlign: "left", py: 0.75, px: 1.5, whiteSpace: "normal" }}
              >
                <Typography sx={{ fontSize: 13, lineHeight: 1.25 }}>{o.label}</Typography>
                <Typography sx={{ fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.disabled" }}>
                  {o.category}
                </Typography>
              </MenuItem>
            ))
          ) : (
            <Typography sx={{ p: 2, fontSize: 12, color: "text.secondary" }}>No matching questions.</Typography>
          )
        ) : (
          /* Category accordions when not searching */
          (tree || []).map(({ category: cat, entries }) => (
            <Accordion
              key={cat}
              disableGutters
              elevation={0}
              expanded={openCat === cat}
              onChange={() => pickCat(cat)}
              sx={{ "&:before": { display: "none" }, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                sx={{ minHeight: 0, px: 1.5, "& .MuiAccordionSummary-content": { my: 1 } }}
              >
                <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "text.secondary" }}>
                  {cat}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {entries.map((entry) =>
                  entry.grid && entry.children?.length ? (
                    /* Likert-grid → nested sub-dropdown */
                    <Accordion
                      key={entry.id}
                      disableGutters
                      elevation={0}
                      expanded={openGrid === entry.id}
                      onChange={() => setOpenGrid((prev) => (prev === entry.id ? null : entry.id))}
                      sx={{ "&:before": { display: "none" }, bgcolor: "grey.50" }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                        sx={{ minHeight: 0, pl: 2.5, pr: 1.5, "& .MuiAccordionSummary-content": { my: 0.75 } }}
                      >
                        <Typography sx={{ fontSize: 13 }}>{entry.label}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0, bgcolor: "background.paper" }}>
                        <MenuItem
                          selected={value === entry.id}
                          onClick={() => select(entry.id)}
                          sx={{ fontSize: 12.5, py: 0.5, pl: 3.75, width: "100%", justifyContent: "flex-start", textAlign: "left", whiteSpace: "normal", fontStyle: "italic" }}
                        >
                          All - full overview
                        </MenuItem>
                        {entry.children.map((ch) => (
                          <MenuItem
                            key={ch.id}
                            selected={value === ch.id}
                            onClick={() => select(ch.id)}
                            sx={{ fontSize: 12.5, py: 0.5, pl: 3.75, width: "100%", justifyContent: "flex-start", textAlign: "left", whiteSpace: "normal" }}
                          >
                            {ch.label}
                          </MenuItem>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <MenuItem
                      key={entry.id}
                      selected={entry.id === value}
                      onClick={() => select(entry.id)}
                      sx={{ fontSize: 13, py: 0.75, pl: 2.5, width: "100%", justifyContent: "flex-start", textAlign: "left", whiteSpace: "normal" }}
                    >
                      {entry.label}
                    </MenuItem>
                  )
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Popover>
    </>
  );
}

// ─── Build-your-own grid collision helpers ───────────────────────────────────
// Blocks occupy an axis-aligned cell rectangle; these enforce hard no-overlap
// boundaries when moving, resizing, dropping, or adding a block.
const BLK_CLAMP = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const BLK_GEOM = (b) => ({
  col: b.col || 1, row: b.row || 1,
  span: b.span || GRID_COLS, rowSpan: b.rowSpan || defaultRowSpan(b.type),
});
const BLK_OVERLAP = (a, c) =>
  !(a.col + a.span - 1 < c.col || c.col + c.span - 1 < a.col ||
    a.row + a.rowSpan - 1 < c.row || c.row + c.rowSpan - 1 < a.row);
const BLK_COLLIDES = (cand, blocks, ignoreId) =>
  (blocks || []).some((b) => b.id !== ignoreId && BLK_OVERLAP(BLK_GEOM(b), cand));
// First free cell for a span×rowSpan block, scanning from (startRow,startCol)
// row-major and wrapping to the top; null if it doesn't fit anywhere.
const BLK_FREE_SPOT = (span, rowSpan, blocks, ignoreId, startRow = 1, startCol = 1) => {
  const rowOrder = [];
  for (let r = startRow; r <= MAX_ROWS - rowSpan + 1; r++) rowOrder.push(r);
  for (let r = 1; r < startRow; r++) rowOrder.push(r);
  for (const r of rowOrder) {
    const colOrder = [];
    for (let c = startCol; c <= GRID_COLS - span + 1; c++) colOrder.push(c);
    for (let c = 1; c < startCol; c++) colOrder.push(c);
    for (const c of colOrder) {
      if (!BLK_COLLIDES({ col: c, row: r, span, rowSpan }, blocks, ignoreId)) return { col: c, row: r };
    }
  }
  return null;
};

// ─── main component ──────────────────────────────────────────────────────────

export default class ReportPage extends Component {
  state = {
    generating: false,
    // An initialConfig (e.g. launched from the map's "Custom" tab) pre-seeds the
    // builder: mode/customType/drillGroupId/areaScope/selConstituency.
    selConstituency: this.props.initialConfig?.selConstituency || null,
    selectedConstituencies: [],
    // Set of active report types for the current selection step
    selectedReportTypes: new Set(["default"]),
    // McCain only: which voting-intention cuts to produce. "all" is the
    // unfiltered report; the rest are VI_map codes (see segmentConfig.mccain).
    // Each selected entry yields its own report, so picking Labour and Reform
    // queues two separate PDFs.
    selectedViCodes: ["all"],
    reportDataByArea: {},
    nationalByGroup: {},
    loadingData: false,
    demoLifetimeUsed: 0,
    // Report geography: "constituency" (default) | "national" (whole GB)
    areaScope: this.props.initialConfig?.areaScope || "constituency",
    // Studio mode: "templates" (default, pre-made reports) | "custom" (builder)
    mode: this.props.initialConfig?.mode || "templates",
    // Which custom report type is being configured (see CUSTOM_REPORT_TYPES)
    customType: this.props.initialConfig?.customType || null,
    // ── Type 1A: "Extend a report" config ──
    customBaseType: "default",       // which template the notes are attached to
    customNotes: "",                 // analyst summary / recommendations text
    customNotesPosition: "top",      // "top" (executive summary) | "bottom" (notes)
    // ── Type 1B: highlight-question swap (political report) ──
    customHeroGroupId: "vote_intention",
    // ── Type 2: "Question drill-down" config ──
    drillGroupId: this.props.initialConfig?.drillGroupId || null, // question to profile
    drillOptionMode: "auto",         // "auto" (top options) | "manual" (chosen keys)
    drillOptionKeys: [],             // explicit option keys when mode === "manual"
    // ── Type 3: "Build your own" config ──
    buildTitle: "Custom report",
    buildBlocks: [],                 // ordered placed blocks (see BuildYourOwnView)
    buildDraftType: "bar",           // staged block type being configured in the sidebar
    buildDraftGroupId: null,         // staged block's question (bar/map/audiences)
    buildDraftText: "",              // staged note text
    buildDraftSpan: 12,              // staged block column span (12-col grid: 3/6/9/12 = ¼/½/¾/full)
  };

  // Transient drag payload for the build canvas (not state — no re-render needed).
  buildDrag = null; // { source: "palette", block } | { source: "placed", id }

  componentDidMount() {
    this.loadDemoState();
    // A launch config is one-shot: consume it so a later plain visit to the
    // Reports section doesn't reopen the same drill-down.
    if (this.props.initialConfig) this.props.onConsumeInitialConfig?.();
  }

  componentDidUpdate(prevProps, prevState) {
    const selectionChanged =
      prevState.selectedConstituencies !== this.state.selectedConstituencies;
    const typeChanged =
      prevState.selectedReportTypes !== this.state.selectedReportTypes;
    if (selectionChanged || typeChanged) {
      this.fetchClientReportData();
    }
    if (selectionChanged && this.props.isDemoUser) {
      this.persistDemoQueue();
    }
    if (prevProps.isDemoUser !== this.props.isDemoUser) {
      this.loadDemoState();
    }
  }

  loadDemoState = async () => {
    if (!this.props.isDemoUser) {
      this.setState({ demoLifetimeUsed: 0 });
      return;
    }
    const queueRaw = window.localStorage.getItem(DEMO_QUEUE_KEY) || "[]";
    const usedRaw = window.localStorage.getItem(DEMO_USED_KEY) || "0";
    let queue = [];
    let used = 0;
    try { queue = JSON.parse(queueRaw); } catch { queue = []; }
    used = Number.parseInt(usedRaw, 10);
    if (!Number.isFinite(used) || used < 0) used = 0;
    const safeQueue = Array.isArray(queue) ? queue.filter((r) => r && r.areaCode && r.reportType).slice(0, DEMO_LIFETIME_LIMIT) : [];
    this.setState({
      selectedConstituencies: safeQueue,
      demoLifetimeUsed: Math.max(used, safeQueue.length),
    });
  };

  persistDemoQueue = () => {
    if (!this.props.isDemoUser) return true;
    const queue = (this.state.selectedConstituencies || []).slice(0, DEMO_LIFETIME_LIMIT);
    window.localStorage.setItem(DEMO_QUEUE_KEY, JSON.stringify(queue));
    return true;
  };

  consumeDemoSlots = async (count) => {
    if (!this.props.isDemoUser) return true;
    const next = Math.min(DEMO_LIFETIME_LIMIT, (this.state.demoLifetimeUsed || 0) + Math.max(0, Number(count) || 0));
    window.localStorage.setItem(DEMO_USED_KEY, String(next));
    this.setState((prev) => ({
      demoLifetimeUsed: Math.min(DEMO_LIFETIME_LIMIT, (prev.demoLifetimeUsed || 0) + Math.max(0, Number(count) || 0)),
    }));
    return true;
  };

  getClientGroupIds = () => {
    const tpl = this.getClientTemplate();
    if (!tpl) return [];
    return tpl.sections.flatMap(s => s.items.map(i => i.groupId));
  };

  groupIdsToKeys = (groupIds) => {
    const { groupsById } = this.props;
    return groupIds.flatMap(id => groupsById?.[id]?.keys || []);
  };

  isOptionEqualToValue = (opt, val) =>
    (opt?.area_cd || opt?.code) === (val?.area_cd || val?.code);

  handleSelect = (_, newValue) => {
    this.setState({ selConstituency: newValue });
  };

  toggleMode = () => {
    this.setState(prev => ({
      mode: prev.mode === "custom" ? "templates" : "custom",
      // Reset the in-progress custom selection when leaving custom mode
      customType: prev.mode === "custom" ? null : prev.customType,
    }));
  };

  selectCustomType = (type) => {
    if (CUSTOM_REPORT_TYPES[type]?.comingSoon) return;
    // "Build your own" is gated to Stack Data Strategy for now.
    if (type === "build" && String(this.props.clientSlug || "").toLowerCase() !== "stack-data-strategy") return;
    this.setState(prev => ({ customType: prev.customType === type ? null : type }));
  };

  toggleReportType = (type) => {
    // Client type stays locked - no toggling
    if (REPORT_TYPE_CONFIG[type]?.comingSoon) return;
    this.setState(prev => {
      const next = new Set(prev.selectedReportTypes);
      if (next.has(type)) {
        // Keep at least one type selected
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }
      return { selectedReportTypes: next };
    });
  };

  // McCain VI picker. "all" and the party codes coexist freely: selecting
  // All voters + Labour + Reform queues three reports. Unlike the report-type
  // toggle this may fall to zero, which disables the add button instead.
  toggleViCode = (code) => {
    this.setState(prev => {
      const has = prev.selectedViCodes.includes(code);
      return {
        selectedViCodes: has
          ? prev.selectedViCodes.filter(c => c !== code)
          : [...prev.selectedViCodes, code],
      };
    });
  };

  // The selected VI cuts as full {code, label, color} objects, in picker order
  // rather than click order, so a queue of reports reads consistently.
  getSelectedViCuts = () =>
    MCCAIN_VI_OPTIONS.filter(o => this.state.selectedViCodes.includes(o.code));

  handleAddConstituency = async () => {
    const { isDemoUser = false } = this.props;
    const { areaScope, selConstituency, selectedConstituencies, selectedReportTypes, demoLifetimeUsed } = this.state;
    const national = areaScope === "national";
    if (!national && !selConstituency) return;
    const areaCode = national ? "GB" : (selConstituency.area_cd || selConstituency.code);
    const name = national ? "Great Britain" : selConstituency.name;
    if (!areaCode) return;
    const remainingSlots = Math.max(0, DEMO_LIFETIME_LIMIT - Number(demoLifetimeUsed || 0));
    if (isDemoUser && remainingSlots <= 0) return;
    const selectedTypes = Array.from(selectedReportTypes)
      .filter((t) => !national || ["default", "business", "client", "motability", "waspi"].includes(t)); // national-supported types
    const nextTypes = isDemoUser ? selectedTypes.slice(0, remainingSlots) : selectedTypes;

    // Every type produces one report except McCain, which produces one per
    // selected voting-intention cut. Expanding into (type, vi) pairs up front
    // keeps the dedupe check and the demo-slot accounting honest about how
    // many reports are really being queued.
    const viCuts = this.getSelectedViCuts();
    const jobs = nextTypes.flatMap((reportType) =>
      reportType === "mccain"
        ? viCuts.map((vi) => ({ reportType, vi }))
        : [{ reportType, vi: null }]
    );

    const toAdd = [];
    for (const { reportType, vi } of jobs) {
      const alreadyAdded = selectedConstituencies.some(
        c => c.areaCode === areaCode && c.reportType === reportType
          && (c.scope || "constituency") === (national ? "national" : "constituency")
          && (c.vi?.code ?? null) === (vi?.code ?? null)
      );
      if (!alreadyAdded) {
        const id = `${areaCode}-${reportType}-${vi?.code ?? "na"}-${Date.now()}-${Math.random()}`;
        toAdd.push({ id, areaCode, name, reportType, scope: national ? "national" : "constituency", ...(vi ? { vi } : {}) });
      }
    }

    if (!toAdd.length) return;
    if (isDemoUser) await this.consumeDemoSlots(toAdd.length);

    this.setState({
      selectedConstituencies: [...selectedConstituencies, ...toAdd],
      selConstituency: null,
    });
  };

  // Type 1A - add an "extended" report: a base template + analyst notes.
  // Reuses the normal queue/preview/export path; the item carries a
  // `customType: "extend"` marker and an `annotations` payload.
  handleAddCustomExtend = async () => {
    const { isDemoUser = false } = this.props;
    const {
      areaScope, selConstituency, selectedConstituencies,
      customBaseType, customNotes, customNotesPosition, customHeroGroupId, demoLifetimeUsed,
    } = this.state;
    const national = areaScope === "national";
    if (!national && !selConstituency) return;
    const areaCode = national ? "GB" : (selConstituency.area_cd || selConstituency.code);
    const name = national ? "Great Britain" : selConstituency.name;
    if (!areaCode) return;
    const text = (customNotes || "").trim();
    const heroGroupId = customBaseType === "default" ? customHeroGroupId : "vote_intention";
    const hasHeroSwap = heroGroupId && heroGroupId !== "vote_intention";
    // An extend item is meaningful if it has notes OR a highlight swap.
    if (!text && !hasHeroSwap) return;
    if (isDemoUser) {
      const remaining = Math.max(0, DEMO_LIFETIME_LIMIT - Number(demoLifetimeUsed || 0));
      if (remaining <= 0) return;
    }

    const id = `${areaCode}-${customBaseType}-extend-${Date.now()}-${Math.random()}`;
    const item = {
      id,
      areaCode,
      name,
      scope: national ? "national" : "constituency",
      reportType: customBaseType,           // preview/export switch off the base type
      customType: "extend",
      annotations: { text, position: customNotesPosition },
      heroGroupId,
    };

    if (isDemoUser) await this.consumeDemoSlots(1);

    this.setState({
      selectedConstituencies: [...selectedConstituencies, item],
      selConstituency: null,
      customNotes: "",
    });
  };

  // Type 2 - add a "question drill-down" report.
  handleAddCustomDrilldown = async () => {
    const { isDemoUser = false } = this.props;
    const {
      areaScope, selConstituency, selectedConstituencies,
      drillGroupId, drillOptionMode, drillOptionKeys,
      customNotes, customNotesPosition, demoLifetimeUsed,
    } = this.state;
    if (!drillGroupId) return;
    const national = areaScope === "national";
    if (!national && !selConstituency) return;
    const areaCode = national ? "GB" : (selConstituency.area_cd || selConstituency.code);
    const name = national ? "Great Britain" : selConstituency.name;
    if (!areaCode) return;
    if (isDemoUser) {
      const remaining = Math.max(0, DEMO_LIFETIME_LIMIT - Number(demoLifetimeUsed || 0));
      if (remaining <= 0) return;
    }

    const optionKeys =
      drillOptionMode === "manual" && drillOptionKeys.length ? [...drillOptionKeys] : null;
    const id = `${areaCode}-drilldown-${drillGroupId}-${Date.now()}-${Math.random()}`;
    const item = {
      id,
      areaCode,
      name,
      scope: national ? "national" : "constituency",
      reportType: "drilldown",
      customType: "drilldown",
      drill: { groupId: drillGroupId, optionKeys },
      annotations: { text: (customNotes || "").trim(), position: customNotesPosition },
    };

    if (isDemoUser) await this.consumeDemoSlots(1);

    this.setState({
      selectedConstituencies: [...selectedConstituencies, item],
      selConstituency: null,
      customNotes: "",
    });
  };

  // ── Type 3: "Build your own" ───────────────────────────────────────────────
  // Turn the staged sidebar config into a block spec.
  makeBuildBlock = () => {
    const { buildDraftType, buildDraftGroupId, buildDraftText, buildDraftSpan } = this.state;
    const def = BUILD_BLOCK_TYPES.find((b) => b.type === buildDraftType);
    if (!def) return null;
    if (def.needsQuestion && !buildDraftGroupId) return null;
    if (buildDraftType === "note" && !buildDraftText.trim()) return null;
    return {
      id: `blk-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: buildDraftType,
      groupId: def.needsQuestion ? buildDraftGroupId : undefined,
      text: buildDraftType === "note" ? buildDraftText.trim() : undefined,
      span: buildDraftSpan,
      rowSpan: defaultRowSpan(buildDraftType),
    };
  };

  // "Add block" button — drop it into the first free cell (top-down), so it
  // never overlaps existing blocks. Does nothing if the page is full.
  addBuildBlock = () => {
    const block = this.makeBuildBlock();
    if (!block) return;
    this.setState((prev) => {
      const span = Math.min(GRID_COLS, block.span || GRID_COLS);
      const rowSpan = Math.min(MAX_ROWS, block.rowSpan || 1);
      const spot = BLK_FREE_SPOT(span, rowSpan, prev.buildBlocks, null, 1, 1);
      if (!spot) return null; // no room left
      return { buildBlocks: [...prev.buildBlocks, { ...block, ...spot, span, rowSpan }] };
    });
  };

  // Sidebar palette → canvas drop: stage the configured block, then place it at
  // the dropped cell — or the nearest free cell if that would overlap.
  handleBuildDragStartPalette = (e) => {
    const block = this.makeBuildBlock();
    if (!block) { e.preventDefault(); return; }
    this.buildDrag = block;
    try { e.dataTransfer.setData("text/plain", "block"); e.dataTransfer.effectAllowed = "copy"; } catch { /* noop */ }
  };

  handleBuildCanvasDrop = (col, row) => {
    const block = this.buildDrag;
    this.buildDrag = null;
    if (!block) return;
    this.setState((prev) => {
      const span = Math.min(GRID_COLS, block.span || GRID_COLS);
      const rowSpan = Math.min(MAX_ROWS, block.rowSpan || 1);
      const c = BLK_CLAMP(col, 1, GRID_COLS - span + 1);
      const r = BLK_CLAMP(row, 1, MAX_ROWS - rowSpan + 1);
      let spot = { col: c, row: r };
      if (BLK_COLLIDES({ ...spot, span, rowSpan }, prev.buildBlocks, null)) {
        spot = BLK_FREE_SPOT(span, rowSpan, prev.buildBlocks, null, r, c);
      }
      if (!spot) return null; // no free cell that fits
      return { buildBlocks: [...prev.buildBlocks, { ...block, ...spot, span, rowSpan }] };
    });
  };

  handleBuildRemove = (id) =>
    this.setState((prev) => ({ buildBlocks: prev.buildBlocks.filter((b) => b.id !== id) }));

  // Merge a geometry patch { col?, row?, span?, rowSpan? } from a move/resize.
  // Kept on the page AND rejected outright if it would overlap another block —
  // so blocks bump against each other (hard boundaries) instead of stacking.
  handleBuildUpdateGeom = (id, patch) =>
    this.setState((prev) => {
      const b = prev.buildBlocks.find((x) => x.id === id);
      if (!b) return null;
      const cand = BLK_GEOM(b);
      if (patch.span != null)    cand.span    = BLK_CLAMP(patch.span, 1, GRID_COLS);
      if (patch.rowSpan != null) cand.rowSpan = BLK_CLAMP(patch.rowSpan, 1, MAX_ROWS);
      if (patch.col != null)     cand.col     = BLK_CLAMP(patch.col, 1, GRID_COLS - cand.span + 1);
      if (patch.row != null)     cand.row     = BLK_CLAMP(patch.row, 1, MAX_ROWS - cand.rowSpan + 1);
      if (BLK_COLLIDES(cand, prev.buildBlocks, id)) return null; // would overlap — reject
      return { buildBlocks: prev.buildBlocks.map((x) => (x.id === id ? { ...x, ...cand } : x)) };
    });

  // Cycle the width through the ¼/½/¾/full presets — skipping any preset that
  // wouldn't fit without overlapping a neighbour (or running off the page).
  handleBuildCycleSpan = (id) =>
    this.setState((prev) => {
      const b = prev.buildBlocks.find((x) => x.id === id);
      if (!b) return null;
      const cur = BLK_GEOM(b);
      const spans = WIDTH_PRESETS.map((p) => p.span);
      let idx = (spans.indexOf(cur.span) + 1) % spans.length;
      for (let tries = 0; tries < spans.length; tries++, idx = (idx + 1) % spans.length) {
        const span = spans[idx];
        const col = Math.min(cur.col, GRID_COLS - span + 1);
        if (!BLK_COLLIDES({ ...cur, span, col }, prev.buildBlocks, id)) {
          return { buildBlocks: prev.buildBlocks.map((x) => (x.id === id ? { ...x, span, col } : x)) };
        }
      }
      return null; // no preset width fits here
    });

  buildEditHandlers = () => ({
    onUpdateGeom: this.handleBuildUpdateGeom,
    onCanvasDrop: this.handleBuildCanvasDrop,
    onRemove: this.handleBuildRemove,
    onCycleSpan: this.handleBuildCycleSpan,
  });

  handleAddCustomBuild = async () => {
    const { isDemoUser = false } = this.props;
    const { areaScope, selConstituency, selectedConstituencies, buildBlocks, buildTitle, demoLifetimeUsed } = this.state;
    const national = areaScope === "national";
    if (!national && !selConstituency) return;
    if (!buildBlocks.length) return;
    const areaCode = national ? "GB" : (selConstituency.area_cd || selConstituency.code);
    const name = national ? "Great Britain" : selConstituency.name;
    if (!areaCode) return;
    if (isDemoUser) {
      const remaining = Math.max(0, DEMO_LIFETIME_LIMIT - Number(demoLifetimeUsed || 0));
      if (remaining <= 0) return;
    }
    const id = `${areaCode}-build-${Date.now()}-${Math.random()}`;
    const item = {
      id, areaCode, name,
      scope: national ? "national" : "constituency",
      reportType: "build",
      customType: "build",
      build: { title: (buildTitle || "Custom report").trim(), blocks: buildBlocks },
    };
    if (isDemoUser) await this.consumeDemoSlots(1);
    this.setState({ selectedConstituencies: [...selectedConstituencies, item] });
  };

  removeConstituency = (id) => {
    this.setState(prev => ({
      selectedConstituencies: prev.selectedConstituencies.filter(c => c.id !== id),
    }));
  };

  clearSelection = () =>
    this.setState({ selectedConstituencies: [], selConstituency: null });

  // Resolve a queued item to the ordered list of nodes to rasterise.
  // A multi-page report exposes report-content-<id>-p1, -p2, … ; a single-page
  // one exposes report-content-<id> and nothing else, so every existing report
  // type keeps the exact node it had before.
  resolveReportNodes = (id) => {
    const pages = [];
    for (let p = 1; ; p++) {
      const el = document.getElementById(`report-content-${id}-p${p}`);
      if (!el) break;
      pages.push(el);
    }
    if (pages.length) return pages;
    const single = document.getElementById(`report-content-${id}`);
    return single ? [single] : [];
  };

  generatePdfFor = async (item) => {
    this.setState({ generating: true });
    try {
      const nodes = this.resolveReportNodes(item.id);
      if (!nodes.length) { this.setState({ generating: false }); return; }
      const pdf = new jsPDF("p", "pt", "a4");
      const imgWidth = 595;
      const pageHeight = 842;
      for (let i = 0; i < nodes.length; i++) {
        const element = nodes[i];
        await waitForImages(element);
        const canvas = await html2canvas(element, {
          scale: 2, useCORS: true, allowTaint: false, backgroundColor: "#ffffff",
        });
        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        // Between nodes only, never after the last one, or the PDF ends blank.
        if (i > 0) pdf.addPage();
        // A tile already matching A4 aspect lands in one page and skips the
        // loop; the slicing is kept for the single-node legacy callers whose
        // capture target is the taller preview card.
        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
      }
      const safeName = item.name.replace(/\s+/g, "_");
      const fileName = item.customType === "drilldown"
        ? `ConstituencyProfile_Drilldown_${safeName}.pdf`
        : item.reportType === "business"
        ? `ConstituencyProfile_Business_${safeName}.pdf`
        // McCain exports one file per VI cut, so the party has to be in the
        // name or the second download overwrites the first.
        : item.reportType === "mccain"
        ? `ConstituencyProfile_McCain_${safeName}_${(item.vi?.label || "All").replace(/\s+/g, "_")}.pdf`
        : `ConstituencyProfile_${safeName}.pdf`;
      pdf.save(fileName);
    } finally {
      this.setState({ generating: false });
    }
  };

  generateAll = async () => {
    const { selectedConstituencies } = this.state;
    if (!selectedConstituencies.length) return;
    for (const item of selectedConstituencies) {
      await this.generatePdfFor(item);
    }
  };

  fetchClientReportData = async () => {
    const { groupsById } = this.props;
    const { selectedConstituencies, selectedReportTypes } = this.state;
    const hasClientOrDefault =
      selectedReportTypes.has("client") || selectedReportTypes.has("default");
    if (!hasClientOrDefault) return;
    if (!selectedConstituencies.length) return;

    const groupIds = this.getClientGroupIds();
    const groupKeys = this.groupIdsToKeys(groupIds);

    const missing = groupIds.filter(id => !(groupsById?.[id]?.keys?.length));
    if (missing.length) {
      console.warn("[ReportPage] missing groupIds in groupsById:", missing);
    }

    this.setState({ loadingData: true });

    try {
      const areaCodes = selectedConstituencies.map(c => c.areaCode).filter(Boolean);
      // Build key batches by schema so mixed-schema templates resolve correctly.
      const schemaToKeys = {};
      groupIds.forEach((gid) => {
        const group = groupsById?.[gid];
        const schema = group?.schema || "tracking";
        const keys = group?.keys || [];
        if (!schemaToKeys[schema]) schemaToKeys[schema] = [];
        schemaToKeys[schema].push(...keys);
      });
      Object.keys(schemaToKeys).forEach((schema) => {
        schemaToKeys[schema] = Array.from(new Set(schemaToKeys[schema]));
      });

      const fetchArrowRows = async (codes, keys, schema) => {
        if (!keys?.length) return [];
        const res = await fetch(`/api/constituency_data_arrow/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            areaCodes: codes,
            theme: "pol",
            groupKeys: keys,
            filters: {},
            ...schemaRequestFields(schema),
            normalise_over_options: false,
          }),
        });
        if (!res.ok) throw new Error(`client report fetch ${res.status} [schema=${schema}]`);
        const buf = await res.arrayBuffer();
        return rowsFromTable(tableFromIPC(buf));
      };

      const byAreaMerged = {};
      const allRows = [];
      for (const [schema, keys] of Object.entries(schemaToKeys)) {
        const schemaRows = await fetchArrowRows(areaCodes, keys, schema);
        schemaRows.forEach((row) => {
          const areaId = String(row?.PCON24CD ?? row?.area_cd ?? row?.area_id ?? "");
          if (!areaId) return;
          if (!byAreaMerged[areaId]) byAreaMerged[areaId] = { ...row };
          else byAreaMerged[areaId] = { ...byAreaMerged[areaId], ...row };
        });

        const natRows = await fetchArrowRows([], keys, schema);
        allRows.push(...natRows);
      }

      // Weighted, not a plain mean over the 632 area rows: a small seat must
      // not count as much as a large one, and each key uses the weight column
      // its percentage was computed over (n_voters for vote share, n_raked
      // otherwise). Same basis as the map and sidebar — see utils/keyWeights.js.
      const nationalByKey = weightedMeanByKey(allRows, groupKeys);

      const byArea = {};
      for (const areaCode of areaCodes) {
        const r = byAreaMerged[String(areaCode)] || null;
        byArea[areaCode] = {};
        groupIds.forEach(gid => {
          const g = groupsById[gid];
          const items = (g?.keys || []).map(k => ({
            key: k,
            label: g?.full_labels?.[k] || g?.labels?.[k] || k,
            national: nationalByKey[k],
            constituency: r?.[k],
          }));
          byArea[areaCode][gid] = items;
        });
      }

      console.log("[ReportPage] client report data sample:", byArea[areaCodes[0]]);
      this.setState({ reportDataByArea: byArea });
    } catch (e) {
      console.error("[ReportPage] fetchClientReportData error:", e);
    } finally {
      this.setState({ loadingData: false });
    }
  };

  getClientTemplate = () => {
    const { clientSlug } = this.props;
    const tpl = REPORT_TEMPLATES[clientSlug];
    return tpl || null;
  };

  // Shared question-picker data (flat search list + category tree with likert
  // grids nested as sub-dropdowns). Used by BOTH the drill-down and build-your-own
  // configs so their question selectors look and behave identically.
  getQuestionPickerData = () => {
    const { groupsById = {} } = this.props;
    const rawGroups = Object.entries(groupsById).filter(([, g]) => Array.isArray(g?.keys) && g.keys.length);

    // Sub-question labels drop any "Prefix: " stem (e.g. "Trust: Banks" → "Banks").
    const cleanSubLabel = (label) => {
      const i = String(label).indexOf(": ");
      return i >= 0 ? String(label).slice(i + 2) : label;
    };
    const gridChildIds = new Set();
    const gridEntries = rawGroups
      .filter(([, g]) => g.type === "likert_grid")
      .map(([id, g]) => {
        const seen = new Set();
        const children = [];
        (g.subVariables || []).forEach((tok) => {
          if (groupsById[tok]?.keys?.length && !seen.has(tok)) {
            seen.add(tok);
            children.push({ id: tok, label: g.subLabels?.[tok] || cleanSubLabel(groupsById[tok]?.label || tok) });
          }
        });
        rawGroups.forEach(([cid, cg]) => {
          if (cid === id || seen.has(cid)) return;
          if (cg.type === "likert" && cg.hidden && cid.startsWith(id + "_")) {
            seen.add(cid);
            children.push({ id: cid, label: cleanSubLabel(cg.label || cid) });
          }
        });
        children.forEach((ch) => gridChildIds.add(ch.id));
        return { grid: true, id, label: g.label || id, category: g.category || "Other", children };
      });

    const questionOptions = rawGroups
      .map(([id, g]) => ({ id, label: g.label || id, category: g.category || "Other" }))
      .sort((a, b) => (a.category + a.label).localeCompare(b.category + b.label));

    const catMap = {};
    rawGroups.forEach(([id, g]) => {
      if (g.type === "likert_grid" || gridChildIds.has(id)) return;
      const cat = g.category || "Other";
      const c = (catMap[cat] = catMap[cat] || { items: [], grids: [] });
      c.items.push({ id, label: g.label || id });
    });
    gridEntries.forEach((gr) => {
      const c = (catMap[gr.category] = catMap[gr.category] || { items: [], grids: [] });
      c.grids.push(gr);
    });
    const questionTree = Object.keys(catMap).sort().map((cat) => ({
      category: cat,
      entries: [
        ...catMap[cat].items.sort((a, b) => a.label.localeCompare(b.label)),
        ...catMap[cat].grids.sort((a, b) => a.label.localeCompare(b.label)),
      ],
    }));

    return { questionOptions, questionTree };
  };

  // ── Type 3 config: compose a report from draggable blocks ──────────────────
  renderBuildConfig = () => {
    const { isDemoUser = false } = this.props;
    const {
      areaScope, selConstituency, buildTitle, buildBlocks,
      buildDraftType, buildDraftGroupId, buildDraftText, buildDraftSpan, demoLifetimeUsed,
    } = this.state;
    const national = areaScope === "national";
    const hasArea = national || !!selConstituency;

    const def = BUILD_BLOCK_TYPES.find((b) => b.type === buildDraftType);
    const needsQuestion = !!def?.needsQuestion;

    // Same tidy question selector as the drill-down config (category tree + search
    // + likert grids as sub-dropdowns).
    const { questionOptions, questionTree } = this.getQuestionPickerData();

    const draftReady = needsQuestion ? !!buildDraftGroupId : (buildDraftType !== "note" || !!buildDraftText.trim());
    const demoRemaining = Math.max(0, DEMO_LIFETIME_LIMIT - Number(demoLifetimeUsed || 0));
    const canAdd = hasArea && buildBlocks.length > 0 && (!isDemoUser || demoRemaining > 0);

    return (
      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Report title */}
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>Report title</Typography>
          <TextField
            fullWidth size="small" value={buildTitle}
            onChange={(e) => this.setState({ buildTitle: e.target.value })}
            placeholder="Custom report"
          />
        </Box>

        {!hasArea && (
          <Box sx={{ p: 1.5, borderRadius: 2, border: "1px dashed", borderColor: "divider", bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, lineHeight: 1.45 }}>
              Choose an area in step 1 to build and preview your report.
            </Typography>
          </Box>
        )}

        {/* Block builder */}
        <Box sx={{ p: 1.25, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
          <Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: 0.75 }}>Add a block</Typography>

          <TextField
            select fullWidth size="small" value={buildDraftType}
            onChange={(e) => this.setState({ buildDraftType: e.target.value, buildDraftGroupId: null })}
            SelectProps={NICE_MENU_PROPS} sx={{ mb: 1 }}
          >
            {BUILD_BLOCK_TYPES.map((b) => (
              <MenuItem key={b.type} value={b.type}>{b.label}</MenuItem>
            ))}
          </TextField>

          {needsQuestion && (
            <Box sx={{ mb: 1 }}>
              <QuestionPicker
                options={questionOptions}
                tree={questionTree}
                value={buildDraftGroupId}
                onChange={(id) => this.setState({ buildDraftGroupId: id || null })}
              />
            </Box>
          )}

          {buildDraftType === "note" && (
            <TextField
              fullWidth size="small" multiline minRows={2} value={buildDraftText}
              onChange={(e) => this.setState({ buildDraftText: e.target.value })}
              placeholder="Note text…" sx={{ mb: 1 }}
            />
          )}

          {/* Starting width (drag the block's right edge on the canvas to fine-tune) */}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 10, mb: 0.5 }}>Starting width</Typography>
          <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
            {WIDTH_PRESETS.map((w) => (
              <Box
                key={w.span}
                onClick={() => this.setState({ buildDraftSpan: w.span })}
                sx={{
                  flex: 1, textAlign: "center", py: 0.5, borderRadius: 1.5, cursor: "pointer",
                  fontSize: 11, fontWeight: 700, border: "1px solid",
                  borderColor: buildDraftSpan === w.span ? "#0abfbc" : "divider",
                  bgcolor: buildDraftSpan === w.span ? "rgba(10,191,188,0.08)" : "#fff",
                  color: buildDraftSpan === w.span ? "#0b7f88" : "text.secondary",
                }}
              >
                {w.label}
              </Box>
            ))}
          </Stack>

          {/* Draggable staged block + add button */}
          <Box
            draggable={draftReady && hasArea}
            onDragStart={this.handleBuildDragStartPalette}
            sx={{
              display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.85, mb: 1,
              borderRadius: 1.5, border: "1px dashed",
              borderColor: draftReady ? "#0abfbc" : "divider",
              bgcolor: draftReady ? "rgba(10,191,188,0.06)" : "#fff",
              cursor: draftReady && hasArea ? "grab" : "default",
              opacity: draftReady ? 1 : 0.6,
            }}
          >
            <DragIndicatorRoundedIcon sx={{ fontSize: 16, color: "#0abfbc" }} />
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              {draftReady ? "Drag onto the report →" : "Configure the block above"}
            </Typography>
          </Box>

          <Button
            fullWidth size="small" variant="outlined" disabled={!draftReady || !hasArea}
            onClick={this.addBuildBlock} startIcon={<AddRoundedIcon />}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Add block to report
          </Button>
        </Box>

        {/* Placed blocks summary */}
        {buildBlocks.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
            {buildBlocks.length} block{buildBlocks.length === 1 ? "" : "s"} placed. <b>Drag</b> to move, drag any <b>edge or corner</b> to resize. Blocks can't overlap — they stop at each other's edges.
          </Typography>
        )}

        {/* Add to queue */}
        <Button
          fullWidth variant="contained" disabled={!canAdd}
          onClick={this.handleAddCustomBuild} startIcon={<CheckCircleRoundedIcon />}
          sx={{ bgcolor: "#111827", "&:hover": { bgcolor: "#0f172a" }, borderRadius: 2 }}
        >
          Add to report
        </Button>
        {buildBlocks.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", fontSize: 10 }}>
            Add at least one block to build this report.
          </Typography>
        )}
        {isDemoUser && demoRemaining <= 0 && (
          <Typography variant="caption" color="text.secondary">Demo limit reached for this account.</Typography>
        )}
      </Box>
    );
  };

  // ── Custom builder (Step 2 replacement when mode === "custom") ──────────────
  // Scaffold only: presents the three custom report types. Per-type
  // configuration UI is filled in by later build phases.
  renderCustomBuilder = () => {
    const { customType } = this.state;
    // "Build your own" is limited to Stack Data Strategy for now.
    const isStackUser = String(this.props.clientSlug || "").toLowerCase() === "stack-data-strategy";
    return (
      <Box>
        <StepLabel number={2} label="Choose a custom report" />

        {!customType ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Adapt a template or drill into a single question.
            </Typography>
            <Stack spacing={1}>
              {Object.entries(CUSTOM_REPORT_TYPES)
                .filter(([type]) => type !== "build" || isStackUser)
                .map(([type, cfg]) => {
                const isLocked = cfg.comingSoon;
                return (
                  <Paper
                    key={type}
                    variant="outlined"
                    onClick={() => this.selectCustomType(type)}
                    sx={{
                      p: 1.25,
                      cursor: isLocked ? "default" : "pointer",
                      borderRadius: 2,
                      border: "0.5px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      opacity: isLocked ? 0.55 : 1,
                      transition: "border-color 0.15s, background-color 0.15s",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      "&:hover": !isLocked ? { borderColor: "text.secondary" } : {},
                    }}
                  >
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1, bgcolor: "grey.100",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 14, flexShrink: 0,
                    }}>
                      {cfg.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
                          {cfg.title}
                        </Typography>
                        {isLocked && (
                          <Chip label="Soon" size="small" sx={{ height: 15, fontSize: 8, "& .MuiChip-label": { px: 0.75 } }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9.5, display: "block", lineHeight: 1.3 }}>
                        {cfg.desc}
                      </Typography>
                    </Box>
                    {isLocked && (
                      <Box sx={{ position: "absolute", top: 6, right: 6 }}>
                        <LockOutlinedIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          </>
        ) : (
          <>
            {/* Compact selected-type header with a way back to the chooser */}
            <Paper
              variant="outlined"
              sx={{ p: 0.75, mb: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box sx={{
                width: 24, height: 24, borderRadius: 1, bgcolor: "grey.100",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, flexShrink: 0,
              }}>
                {CUSTOM_REPORT_TYPES[customType].icon}
              </Box>
              <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, flex: 1, minWidth: 0 }}>
                {CUSTOM_REPORT_TYPES[customType].title}
              </Typography>
              <Button
                size="small"
                startIcon={<ChevronLeftIcon sx={{ fontSize: 14 }} />}
                onClick={() => this.setState({ customType: null })}
                sx={{ fontSize: 10, color: "text.secondary", minWidth: 0, px: 0.75 }}
              >
                Change
              </Button>
            </Paper>

            {customType === "extend" && this.renderExtendConfig()}
            {customType === "drilldown" && this.renderDrilldownConfig()}
            {customType === "build" && this.renderBuildConfig()}
          </>
        )}
      </Box>
    );
  };

  // ── Type 2 config: pick a question + which options to profile ───────────────
  renderDrilldownConfig = () => {
    const { isDemoUser = false, groupsById = {} } = this.props;
    const { areaScope, selConstituency, drillGroupId, drillOptionMode, drillOptionKeys, demoLifetimeUsed } = this.state;
    const national = areaScope === "national";
    const hasArea = national || !!selConstituency;
    const areaLabel = national ? "Great Britain" : selConstituency?.name;

    // Shared tidy question selector (category tree + search + grid sub-dropdowns).
    const { questionOptions, questionTree } = this.getQuestionPickerData();

    const group = drillGroupId ? groupsById[drillGroupId] : null;
    // For a likert grid the selectable "responses" are the sub-variables
    // (e.g. sectors), not the flat matrix keys.
    const isGrid = group?.type === "likert_grid";
    const optionEntries = isGrid
      ? (group?.subVariables || []).map((s) => ({ key: s, label: group.subLabels?.[s] || s }))
      : (group?.keys || []).map((k) => ({ key: k, label: group?.labels?.[k] || k }));

    const demoRemaining = Math.max(0, DEMO_LIFETIME_LIMIT - Number(demoLifetimeUsed || 0));
    const canAdd = hasArea && !!drillGroupId && (!isDemoUser || demoRemaining > 0);

    const toggleOptionKey = (key) => {
      this.setState((prev) => {
        const set = new Set(prev.drillOptionKeys);
        if (set.has(key)) set.delete(key); else set.add(key);
        return { drillOptionKeys: Array.from(set) };
      });
    };

    return (
      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Question picker */}
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.75 }}>
            Question
          </Typography>
          <QuestionPicker
            options={questionOptions}
            tree={questionTree}
            value={drillGroupId}
            onChange={(id) => this.setState({ drillGroupId: id || null, drillOptionKeys: [] })}
          />
        </Box>

        {!hasArea ? (
          <Box sx={{ p: 1.5, borderRadius: 2, border: "1px dashed", borderColor: "divider", bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, lineHeight: 1.45 }}>
              Choose an area in step 1 {drillGroupId ? "to preview the drill-down." : "and a question above to preview the drill-down."}
            </Typography>
          </Box>
        ) : group ? (
          <>
            {/* Options to profile */}
            <Box>
              <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.75 }}>
                Audiences for
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                {[
                  { key: "auto", label: "Top responses" },
                  { key: "manual", label: "Choose responses" },
                ].map((opt) => {
                  const isSelected = drillOptionMode === opt.key;
                  return (
                    <Paper
                      key={opt.key}
                      variant="outlined"
                      onClick={() => this.setState({ drillOptionMode: opt.key })}
                      sx={{
                        flex: 1, py: 0.75, textAlign: "center", cursor: "pointer", borderRadius: 2,
                        border: isSelected ? "1.5px solid #111827" : "0.5px solid",
                        borderColor: isSelected ? "#111827" : "divider",
                        bgcolor: isSelected ? "grey.50" : "background.paper",
                      }}
                    >
                      <Typography sx={{ fontSize: 10, fontWeight: 500 }}>{opt.label}</Typography>
                    </Paper>
                  );
                })}
              </Stack>

              {drillOptionMode === "manual" && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {optionEntries.map((entry) => {
                    const k = entry.key;
                    const isSelected = drillOptionKeys.includes(k);
                    const label = entry.label;
                    return (
                      <Chip
                        key={k}
                        label={label}
                        size="small"
                        onClick={() => toggleOptionKey(k)}
                        variant={isSelected ? "filled" : "outlined"}
                        sx={{
                          fontSize: 10, height: 22, cursor: "pointer",
                          bgcolor: isSelected ? "#111827" : "transparent",
                          "& .MuiChip-label": { color: isSelected ? "#fff" : "text.primary" },
                          "&:hover": { bgcolor: isSelected ? "#0f172a" : "grey.100" },
                        }}
                      />
                    );
                  })}
                  {drillOptionMode === "manual" && drillOptionKeys.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9.5, mt: 0.5 }}>
                      Pick one or more responses, or switch to “Top responses”.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Shared note accordion */}
            {this.renderNotesAccordion()}

            {/* Add */}
            <Button
              fullWidth
              variant="contained"
              disabled={!canAdd}
              onClick={this.handleAddCustomDrilldown}
              startIcon={<CheckCircleRoundedIcon />}
              sx={{ bgcolor: "#111827", "&:hover": { bgcolor: "#0f172a" }, borderRadius: 2 }}
            >
              Add to report
            </Button>
            {!drillGroupId && (
              <Typography variant="caption" color="text.secondary">
                Choose a question above to add this report.
              </Typography>
            )}
            {isDemoUser && demoRemaining <= 0 && (
              <Typography variant="caption" color="text.secondary">
                Demo limit reached for this account.
              </Typography>
            )}
          </>
        ) : (
          <Box sx={{ p: 1.5, borderRadius: 2, border: "1px dashed", borderColor: "divider", bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, lineHeight: 1.45 }}>
              Choose a question above to preview the drill-down for <strong>{areaLabel}</strong>.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Shared "Add a note" accordion - available on every custom report type.
  renderNotesAccordion = () => {
    const { customNotes, customNotesPosition } = this.state;
    const hasNotes = customNotes.trim().length > 0;
    return (
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: "1px solid", borderColor: "divider", borderRadius: 2,
          bgcolor: "background.paper", overflow: "hidden",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{ minHeight: 0, px: 1.25, "& .MuiAccordionSummary-content": { my: 1 } }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <StickyNote2OutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
              Add a note
            </Typography>
            {hasNotes && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#0abfbc" }} />}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 1.25, pb: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <TextField
            multiline
            minRows={3}
            maxRows={9}
            fullWidth
            size="small"
            placeholder="Add an executive summary, key takeaways or recommendations to appear on the report…"
            value={customNotes}
            onChange={(e) => this.setState({ customNotes: e.target.value })}
          />
          {hasNotes && (
            <TextField
              select
              fullWidth
              size="small"
              label="Placement"
              value={customNotesPosition}
              onChange={(e) => this.setState({ customNotesPosition: e.target.value })}
              SelectProps={NICE_MENU_PROPS}
            >
              <MenuItem value="top">Top - executive summary</MenuItem>
              <MenuItem value="bottom">Bottom - analyst notes</MenuItem>
            </TextField>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // ── Type 1A config: base template + highlight swap ─────────────────────────
  renderExtendConfig = () => {
    const { isDemoUser = false } = this.props;
    const { areaScope, selConstituency, customBaseType, customNotes, customHeroGroupId, demoLifetimeUsed } = this.state;
    const isHolidayTaxClient = HOLIDAY_TAX_CLIENTS.includes(this.props.clientSlug);
    const isMotabilityClient = MOTABILITY_CLIENTS.includes(this.props.clientSlug);
    const isWaspiClient = WASPI_CLIENTS.includes(this.props.clientSlug);
    const national = areaScope === "national";

    const baseTypes = Object.entries(REPORT_TYPE_CONFIG).filter(([type]) => {
      if (type === "client" && !isHolidayTaxClient) return false;
      if (type === "motability" && !isMotabilityClient) return false; // Motability clients only
      if (type === "waspi" && !isWaspiClient) return false; // WASPI clients only
      // McCain is excluded from "adapt an existing template" entirely: an
      // extend item carries no VI cut, so there is no party to report on.
      if (type === "mccain") return false;
      // National layouts exist for Political, Business, Holiday Tax and Motability.
      if (national && !["default", "business", "client", "motability", "waspi"].includes(type)) return false;
      return true;
    });

    // Highlight-swap is supported on the political (default) report, at both
    // constituency and national scope.
    const heroSwapSupported = customBaseType === "default";
    const hasHeroSwap = heroSwapSupported && customHeroGroupId !== "vote_intention";

    const demoRemaining = Math.max(0, DEMO_LIFETIME_LIMIT - Number(demoLifetimeUsed || 0));
    const areaReady = national || !!selConstituency;
    const canAdd =
      areaReady &&
      (customNotes.trim().length > 0 || hasHeroSwap) &&
      (!isDemoUser || demoRemaining > 0);

    return (
      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Base report */}
        <Box>
          <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.75 }}>
            Base report
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={customBaseType}
            onChange={(e) => this.setState({ customBaseType: e.target.value })}
            SelectProps={NICE_MENU_PROPS}
          >
            {baseTypes.map(([type, cfg]) => (
              <MenuItem key={type} value={type}>
                <Box component="span" sx={{ mr: 1 }}>{cfg.icon}</Box>{cfg.title}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {!areaReady ? (
          /* Preview-first: prompt for a constituency; the report renders in the
             preview pane before the user configures it. */
          <Box
            sx={{
              p: 1.5, borderRadius: 2, border: "1px dashed", borderColor: "divider",
              bgcolor: "grey.50",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, lineHeight: 1.45 }}>
              Select a constituency in step 1 to preview the <strong>{REPORT_TYPE_CONFIG[customBaseType]?.title}</strong> report, then configure it below.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Highlight question (political report only) */}
            {heroSwapSupported && (
              <Box>
                <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
                  Highlight question
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={customHeroGroupId}
                  onChange={(e) => this.setState({ customHeroGroupId: e.target.value })}
                  SelectProps={NICE_MENU_PROPS}
                >
                  {POLITICAL_HERO_OPTIONS.map((o) => (
                    <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {/* Shared note accordion */}
            {this.renderNotesAccordion()}

            {/* Add */}
            <Button
              fullWidth
              variant="contained"
              disabled={!canAdd}
              onClick={this.handleAddCustomExtend}
              startIcon={<CheckCircleRoundedIcon />}
              sx={{ bgcolor: "#111827", "&:hover": { bgcolor: "#0f172a" }, borderRadius: 2 }}
            >
              Add to report
            </Button>
            {customNotes.trim().length === 0 && !hasHeroSwap && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", fontSize: 10 }}>
                {heroSwapSupported
                  ? "Change the highlight or add a note to build this report."
                  : "Add a note to build this report."}
              </Typography>
            )}
            {isDemoUser && demoRemaining <= 0 && (
              <Typography variant="caption" color="text.secondary">
                Demo limit reached for this account.
              </Typography>
            )}
          </>
        )}
      </Box>
    );
  };

  // Renders the base report view for a queued item (shared by plain template
  // items and "extend" custom items - the notes block is layered on separately).
  // `height` lets an extend item shrink the report so notes fit on one page.
  renderBaseReportView = (c, { height } = {}) => {
    const { extDataBlurb, demographics, mapImages } = this.props;
    if (c.reportType === "business") {
      return <BusinessReportView key={c.id} constName={c.name} areaCode={c.areaCode} height={height} national={c.scope === "national"} />;
    }
    if (c.reportType === "client") {
      return (
        <HolidayTaxReportView
          key={c.id}
          constName={c.name}
          areaCode={c.areaCode}
          majority={extDataBlurb?.majority}
          height={height}
          national={c.scope === "national"}
        />
      );
    }
    if (c.reportType === "motability") {
      return <MotabilityReportView key={c.id} constName={c.name} areaCode={c.areaCode} national={c.scope === "national"} />;
    }
    if (c.reportType === "waspi") {
      return <WaspiReportView key={c.id} constName={c.name} areaCode={c.areaCode} majority={extDataBlurb?.majority} height={height} national={c.scope === "national"} />;
    }
    if (c.reportType === "mccain") {
      // `vi` is what makes two McCain cards for the same seat different reports.
      // idPrefix lets the view id its own pages, which is how the exporter
      // finds page 2.
      return (
        <McCainReportView
          key={c.id}
          constName={c.name}
          areaCode={c.areaCode}
          vi={c.vi}
          height={height}
          idPrefix={`report-content-${c.id}`}
        />
      );
    }
    return (
      <ConstituencyReportView
        key={c.id}
        constName={c.name}
        areaCode={c.areaCode}
        mpName={extDataBlurb?.mp_name}
        party={extDataBlurb?.mp_party}
        roles={extDataBlurb?.mp_roles}
        majority={extDataBlurb?.majority}
        demographics={demographics}
        mapImages={mapImages}
        height={height}
        heroGroupId={c.heroGroupId || "vote_intention"}
        national={c.scope === "national"}
      />
    );
  };

  // Renders one report "page" card in the preview / export area.
  // For "extend" items the card is a fixed A4 page (flex column): the notes
  // block takes its natural height and the report fills the rest via height:100%,
  // so the report squeezes to keep everything on a single exported page.
  // `isDraft` marks the live in-progress extend report (not yet in the queue).
  renderReportCard = (c, { isDraft = false } = {}) => {
    const isDrill = c.customType === "drilldown";
    const isBuild = c.customType === "build";
    const notesText = c.annotations?.text;
    const notesPos = c.annotations?.position || "bottom";
    // Build reports carry their own header/blocks (incl. note blocks) — no wrapper.
    const hasNotes = !!notesText && !isBuild;
    const notes = hasNotes ? <NotesBlock text={notesText} position={notesPos} /> : null;

    // With a note, the card becomes a fixed A4 page and the report fills the
    // remaining height (single-page squeeze). Without one it lays out naturally.
    const body = isDrill ? (
      <QuestionDrilldownView
        key={c.id}
        areaCode={c.areaCode}
        constName={c.name}
        groupId={c.drill?.groupId}
        optionKeys={c.drill?.optionKeys ?? null}
        national={c.scope === "national"}
        height={hasNotes ? "100%" : undefined}
      />
    ) : isBuild ? (
      <BuildYourOwnView
        key={c.id}
        // The live draft edits the builder state directly; a queued item uses its snapshot.
        blocks={isDraft ? this.state.buildBlocks : (c.build?.blocks || [])}
        title={isDraft ? this.state.buildTitle : (c.build?.title || "Custom report")}
        constName={c.name}
        areaCode={c.areaCode}
        national={c.scope === "national"}
        extras={{ extDataBlurb: this.props.extDataBlurb, demographics: this.props.demographics, mapImages: this.props.mapImages }}
        edit={isDraft ? this.buildEditHandlers() : null}
      />
    ) : (
      this.renderBaseReportView(c, { height: hasNotes ? "100%" : undefined })
    );

    return (
      <Paper
        key={`card-${c.id}`}
        id={isDraft ? undefined : `report-content-${c.id}`}
        elevation={3}
        sx={{
          mx: "auto",
          mb: 4,
          p: 2.5,
          width: 840,
          borderRadius: 3,
          scrollSnapAlign: "start",
          position: "relative",
          ...(hasNotes
            ? { height: 1180, display: "flex", flexDirection: "column", overflow: "hidden" }
            : { minHeight: 1180 }),
          ...(isDraft ? { outline: "2px dashed #0abfbc", outlineOffset: 2 } : {}),
        }}
      >
        {isDraft && (
          <Box
            sx={{
              position: "absolute", top: 10, right: 10, zIndex: 5,
              px: 1, py: 0.25, borderRadius: 1,
              bgcolor: "#0abfbc", color: "#fff",
              fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
            }}
          >
            DRAFT - NOT YET ADDED
          </Box>
        )}

        {hasNotes ? (
          <>
            {notesPos === "top" && <Box sx={{ flexShrink: 0 }}>{notes}</Box>}
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {body}
            </Box>
            {notesPos === "bottom" && <Box sx={{ flexShrink: 0 }}>{notes}</Box>}
          </>
        ) : (
          body
        )}
      </Paper>
    );
  };

  // Live draft item while composing a custom report (before it's queued).
  getDraftItem = () => {
    const {
      mode, customType, areaScope, selConstituency,
      customBaseType, customNotes, customNotesPosition,
      drillGroupId, drillOptionMode, drillOptionKeys,
    } = this.state;
    if (mode !== "custom") return null;
    const national = areaScope === "national";
    if (!national && !selConstituency) return null;
    const areaCode = national ? "GB" : (selConstituency.area_cd || selConstituency.code);
    const name = national ? "Great Britain" : selConstituency.name;
    if (!areaCode) return null;
    const scope = national ? "national" : "constituency";

    if (customType === "extend") {
      const { customHeroGroupId } = this.state;
      return {
        id: "__draft__",
        areaCode,
        name,
        scope,
        reportType: customBaseType,
        customType: "extend",
        annotations: { text: (customNotes || "").trim(), position: customNotesPosition },
        heroGroupId: customBaseType === "default" ? customHeroGroupId : "vote_intention",
      };
    }
    if (customType === "drilldown" && drillGroupId) {
      return {
        id: "__draft__",
        areaCode,
        name,
        scope,
        reportType: "drilldown",
        customType: "drilldown",
        drill: {
          groupId: drillGroupId,
          optionKeys: drillOptionMode === "manual" && drillOptionKeys.length ? [...drillOptionKeys] : null,
        },
        annotations: { text: (customNotes || "").trim(), position: customNotesPosition },
      };
    }
    if (customType === "build") {
      // Always show the build canvas (even empty) so users have a drop target.
      return {
        id: "__draft__",
        areaCode,
        name,
        scope,
        reportType: "build",
        customType: "build",
        build: { title: this.state.buildTitle, blocks: this.state.buildBlocks },
      };
    }
    return null;
  };

  render() {
    const { generating, selectedConstituencies, selConstituency, selectedReportTypes, selectedViCodes, demoLifetimeUsed, mode, areaScope } = this.state;
    const {
      constituencyOptions = [],
      isDemoUser = false,
    } = this.props;
    const isHolidayTaxClient = HOLIDAY_TAX_CLIENTS.includes(this.props.clientSlug);
    const isMotabilityClient = MOTABILITY_CLIENTS.includes(this.props.clientSlug);
    const isWaspiClient = WASPI_CLIENTS.includes(this.props.clientSlug);
    const isMccainClient = MCCAIN_CLIENTS.includes(this.props.clientSlug);

    // Steps 2 and 3 stay hidden until there is an area to report on, so the
    // sidebar reads as a sequence rather than presenting every control at once.
    // "National" needs no further choice, so it counts as chosen on its own.
    const areaChosen = areaScope === "national" || !!selConstituency;

    // Footer composition. Templates mode adds from the footer; the custom
    // builder keeps its own add buttons next to the config they depend on.
    // Export only appears once there is a queue, so it is not sitting under the
    // Add button, greyed out, at the exact moment Add is what you want.
    const hasQueue = selectedConstituencies.length > 0;
    const showAddAction = mode === "templates" && areaChosen;
    const showFooter = showAddAction || hasQueue;

    // The VI step only applies to the McCain template, and only at constituency
    // scope (McCain has no national layout).
    const mccainSelected = selectedReportTypes.has("mccain") && areaScope !== "national";

    const demoRemaining = Math.max(0, DEMO_LIFETIME_LIMIT - Number(demoLifetimeUsed || 0));
    const demoCapacityRemaining = demoRemaining > 0;
    const canAdd = (areaScope === "national" || !!selConstituency)
      && selectedReportTypes.size > 0
      && (!mccainSelected || selectedViCodes.length > 0);
    const canAddWithDemoRules = canAdd && (!isDemoUser || demoCapacityRemaining);
    const selectedTypes = Array.from(selectedReportTypes);
    // McCain contributes one report per selected VI cut, not one report.
    const plannedReportCount = selectedTypes.reduce(
      (n, t) => n + (t === "mccain" && mccainSelected ? selectedViCodes.length : 1),
      0
    );
    const addableTypeCount = isDemoUser
      ? Math.min(plannedReportCount, demoRemaining)
      : plannedReportCount;

    // Live draft of an in-progress custom report, shown in the preview so
    // users see the report before committing it.
    const draftItem = this.getDraftItem();

    return (
      <Box sx={{ display: "flex", height: "92vh", p: 2, gap: 2, bgcolor: "#f7f8fa" }}>

        {/* ── Sidebar ── */}
        <Box
          id="tour-report-sidebar"
          sx={{
            width: 360,
            flexShrink: 0,
            bgcolor: "background.paper",
            border: "0.5px solid",
            borderColor: "divider",
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            height: "calc(90vh)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box sx={{ px: 2.5, py: 2.25, borderBottom: "0.5px solid", borderColor: "divider" }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={500}>Report studio</Typography>
                <Typography variant="caption" color="text.secondary">
                  {mode === "custom"
                    ? "Custom builder"
                    : "Build and export constituency reports"}
                </Typography>
              </Box>
              <Tooltip title={mode === "custom" ? "Back to templates" : "Customise a report"}>
                <IconButton
                  size="small"
                  onClick={this.toggleMode}
                  aria-label="Toggle custom report builder"
                  sx={{
                    flexShrink: 0,
                    color: mode === "custom" ? "#fff" : "text.secondary",
                    bgcolor: mode === "custom" ? "#111827" : "transparent",
                    border: "1px solid",
                    borderColor: mode === "custom" ? "#111827" : "divider",
                    borderRadius: 1.5,
                    "&:hover": {
                      bgcolor: mode === "custom" ? "#0f172a" : "grey.100",
                    },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            {isDemoUser && (
              <Box
                sx={{
                  mt: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  border: "1px solid rgba(37,99,235,0.25)",
                  bgcolor: "rgba(37,99,235,0.06)",
                }}
              >
                <Typography sx={{ fontSize: "11px", color: "#1e3a8a", fontWeight: 500 }}>
                  Demo mode: create up to 2 reports. Your reports will be saved for your next login.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Scrollable body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2.5,
              py: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* Step 1 - Area (national or a constituency) */}
            <Box>
              <StepLabel number={1} label="Choose area" />
              <TextField
                select
                fullWidth
                size="small"
                value={areaScope}
                onChange={(e) => this.setState({ areaScope: e.target.value })}
                SelectProps={NICE_MENU_PROPS}
                sx={{ mb: areaScope === "constituency" ? 1 : 0 }}
              >
                <MenuItem value="constituency">A constituency</MenuItem>
                <MenuItem value="national">National (whole of GB)</MenuItem>
              </TextField>
              {areaScope === "constituency" && (
                <Autocomplete
                  options={constituencyOptions}
                  value={selConstituency}
                  onChange={this.handleSelect}
                  isOptionEqualToValue={this.isOptionEqualToValue}
                  getOptionLabel={(option) => option?.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select constituency"
                      placeholder="Type to search…"
                    />
                  )}
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>

            {mode === "templates" ? (
            !areaChosen ? (
              <Typography variant="caption" color="text.secondary">
                Pick a constituency to choose report types.
              </Typography>
            ) : (
            <>
            {/* Step 2 - Report type (multi-select) */}
            <Box>
              <StepLabel number={2} label="Choose report types" />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Select one or more for {areaScope === "national" ? "Great Britain" : "this constituency"}.
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
                {Object.entries(REPORT_TYPE_CONFIG).filter(([type]) => {
                  if (type === "client" && !isHolidayTaxClient) return false;
                  if (type === "motability" && !isMotabilityClient) return false; // Motability clients only
      if (type === "waspi" && !isWaspiClient) return false; // WASPI clients only
                  if (type === "mccain" && !isMccainClient) return false; // McCain clients only
                  // National layouts exist for Political, Business, Holiday Tax and Motability.
                  if (areaScope === "national" && !["default", "business", "client", "motability", "waspi"].includes(type)) return false;
                  return true;
                }).map(([type, cfg]) => {
                  const isSelected = selectedReportTypes.has(type);
                  const demoTypeLocked = isDemoUser && demoRemaining <= 0 && !isSelected;
                  const isLocked = cfg.comingSoon || demoTypeLocked;
                  return (
                    <Paper
                      key={type}
                      variant="outlined"
                      onClick={() => this.toggleReportType(type)}
                      sx={{
                        p: 1.25,
                        cursor: isLocked ? "default" : "pointer",
                        borderRadius: 2,
                        border: isSelected ? "1.5px solid #111827" : "0.5px solid",
                        borderColor: isSelected ? "#111827" : "divider",
                        bgcolor: isSelected ? "grey.50" : "background.paper",
                        opacity: isLocked ? 0.55 : 1,
                        transition: "border-color 0.15s, background-color 0.15s",
                        position: "relative",
                        "&:hover": !isLocked
                          ? { borderColor: isSelected ? "#111827" : "text.secondary" }
                          : {},
                      }}
                    >
                      {isLocked && (
                        <Box sx={{ position: "absolute", top: 5, right: 5 }}>
                          <LockOutlinedIcon sx={{ fontSize: 10, color: "text.disabled" }} />
                        </Box>
                      )}
                      <Box
                        sx={{
                          width: 24, height: 24, borderRadius: 1,
                          bgcolor: cfg.iconBg,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, mb: 0.75,
                        }}
                      >
                        {cfg.icon}
                      </Box>
                      <Typography variant="caption" fontWeight={500} display="block" sx={{ fontSize: "10px" }}>
                        {cfg.title}
                      </Typography>
                      {cfg.comingSoon ? (
                        <Typography variant="caption" sx={{ fontSize: "9px", color: "text.disabled" }}>
                          Coming soon
                        </Typography>
                      ) : demoTypeLocked ? (
                        <Typography variant="caption" sx={{ fontSize: "9px", color: "text.disabled" }}>
                          Demo limit reached
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "9px" }}>
                          {cfg.desc}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            {/* Step 3 - McCain only: which voting-intention cuts to produce.
                Each selection becomes its own report, so Labour + Reform gives
                two PDFs rather than one combined document. */}
            {mccainSelected && (
              <Box>
                <StepLabel number={3} label="Choose voting intention" />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  One McCain report per selection. Figures are among that party's
                  voters in {selConstituency?.name || "this constituency"}.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {MCCAIN_VI_OPTIONS.map((opt) => {
                    const isSelected = selectedViCodes.includes(opt.code);
                    return (
                      <Chip
                        key={opt.code}
                        label={opt.label}
                        size="small"
                        onClick={() => this.toggleViCode(opt.code)}
                        variant={isSelected ? "filled" : "outlined"}
                        sx={{
                          fontSize: "10px",
                          height: 24,
                          cursor: "pointer",
                          borderRadius: 1.5,
                          "& .MuiChip-label": { px: 1 },
                          // A selected party fills solid in its own colour, so
                          // the picker matches the badge on the report it makes.
                          ...(isSelected && opt.code !== "all"
                            ? {
                                bgcolor: opt.color,
                                borderColor: opt.color,
                                color: readableOn(opt.color),
                                fontWeight: 700,
                                "&:hover": { bgcolor: opt.color, opacity: 0.9 },
                              }
                            : {
                                bgcolor: isSelected ? "grey.100" : "transparent",
                                borderColor: isSelected ? "text.secondary" : "divider",
                                color: isSelected ? "text.primary" : "text.secondary",
                                fontWeight: isSelected ? 600 : 400,
                                "&:hover": { bgcolor: isSelected ? "grey.200" : "grey.50" },
                              }),
                        }}
                      />
                    );
                  })}
                </Box>
                {selectedViCodes.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.75, fontSize: "9px" }}>
                    Pick at least one.
                  </Typography>
                )}
              </Box>
            )}

            {/* The Add action lives in the pinned footer alongside Export, so
                it stays reachable without scrolling past the steps. */}
            </>
            )
            ) : (
              this.renderCustomBuilder()
            )}

            <Divider />

            {/* Queue */}
            {selectedConstituencies.length > 0 ? (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" fontWeight={500}>Report queue</Typography>
                    <Chip
                      label={selectedConstituencies.length}
                      size="small"
                      sx={{
                        height: 18, fontSize: 10,
                        bgcolor: "#111827", color: "#fff",
                        borderRadius: "999px",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Stack>
                  <Button
                    size="small"
                    variant="text"
                    onClick={this.clearSelection}
                    disabled={isDemoUser}
                    sx={{ fontSize: 11, color: "text.secondary", minWidth: 0, px: 0.75 }}
                  >
                    Clear all
                  </Button>
                </Stack>

                <Stack spacing={0.75}>
                  {selectedConstituencies.map(c => {
                    const drillLabel = c.customType === "drilldown"
                      ? (this.props.groupsById?.[c.drill?.groupId]?.label || "Question")
                      : null;
                    const cfg = c.customType === "drilldown"
                      ? { title: "Drill-down", dot: "#0abfbc", pillBg: "#ccfbf1", pillText: "#0f766e" }
                      : (REPORT_TYPE_CONFIG[c.reportType] || REPORT_TYPE_CONFIG.default);
                    return (
                      <Paper
                        key={c.id}
                        variant="outlined"
                        sx={{
                          px: 1.5, py: 1,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                              bgcolor: cfg.dot,
                            }}
                          />
                          <Typography variant="caption" fontWeight={500}>{c.name}</Typography>
                          {c.customType === "extend" && (
                            <Tooltip title={
                              [
                                c.annotations?.text ? "analyst notes" : null,
                                c.heroGroupId && c.heroGroupId !== "vote_intention" ? "custom highlight" : null,
                              ].filter(Boolean).join(" + ") || "Extended report"
                            }>
                              <Box component="span" sx={{ fontSize: 11, lineHeight: 1, color: "#0abfbc" }}>✦</Box>
                            </Tooltip>
                          )}
                          {c.customType === "drilldown" && (
                            <Tooltip title={`Drill-down: ${drillLabel}`}>
                              <Box component="span" sx={{ fontSize: 10, lineHeight: 1 }}>🔍</Box>
                            </Tooltip>
                          )}
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          {/* McCain queues one report per VI cut, so without the
                              party the pills for a single seat are identical. */}
                          {c.vi && (
                            <Chip
                              label={c.vi.label}
                              size="small"
                              sx={{
                                height: 18, fontSize: 10, borderRadius: "999px",
                                // Party cuts fill solid so a queue of McCain
                                // reports for one seat is scannable at a glance;
                                // "All voters" stays neutral.
                                ...(c.vi.code === "all"
                                  ? { bgcolor: "transparent", color: "text.secondary", border: "0.5px solid", borderColor: "divider" }
                                  : { bgcolor: c.vi.color, color: readableOn(c.vi.color), fontWeight: 700 }),
                                "& .MuiChip-label": { px: 1 },
                              }}
                            />
                          )}
                          <Chip
                            label={cfg.title}
                            size="small"
                            sx={{
                              height: 18, fontSize: 10, borderRadius: "999px",
                              bgcolor: cfg.pillBg,
                              color: cfg.pillText,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                          <Button
                            size="small"
                            onClick={() => this.removeConstituency(c.id)}
                            disabled={isDemoUser}
                            sx={{ minWidth: 0, p: 0.25 }}
                          >
                            <CloseIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                          </Button>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Search a constituency and choose report types to get started.
              </Typography>
            )}
          </Box>

          {/* Footer - the two actions, pinned together.
              Add used to sit at the bottom of the scrolling body, directly
              above this, so both rendered as identical dark full-width buttons
              in the same spot and Add was only reachable by scrolling. They now
              share one bar: Add is the filled primary (it is pressed
              repeatedly), Export is outlined and only appears once there is
              something queued to export. */}
          {showFooter && (
          <Box
            sx={{
              px: 2.5, py: 2,
              borderTop: "0.5px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {showAddAction && (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!canAddWithDemoRules}
                  onClick={this.handleAddConstituency}
                  startIcon={<CheckCircleRoundedIcon />}
                  sx={{
                    bgcolor: "#111827",
                    "&:hover": { bgcolor: "#0f172a" },
                    borderRadius: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {canAddWithDemoRules && addableTypeCount > 1
                    ? `Add ${addableTypeCount} reports`
                    : "Add to report"}
                </Button>
                {isDemoUser && !demoCapacityRemaining && (
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    Demo limit reached for this account.
                  </Typography>
                )}
              </>
            )}
            {hasQueue && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={
                generating
                  ? <CircularProgress size={14} color="inherit" />
                  : <DownloadIcon />
              }
              disabled={isDemoUser || generating}
              onClick={this.generateAll}
              sx={{
                borderRadius: 2,
                color: "#111827",
                borderColor: "#111827",
                "&:hover": { borderColor: "#0f172a", bgcolor: "grey.50" },
                // A McCain add can queue several reports at once, so the count
                // reaches two digits easily. Without this the label wraps and
                // the footer grows a line.
                whiteSpace: "nowrap",
                ...(isDemoUser
                  ? {
                      color: "#9ca3af",
                      borderColor: "#d1d5db",
                    }
                  : {}),
              }}
            >
              {isDemoUser
                ? "Export disabled in demo"
                : generating
                ? "Exporting…"
                : `Export ${selectedConstituencies.length} report${selectedConstituencies.length !== 1 ? "s" : ""}`
              }
            </Button>
            )}
            {hasQueue && (
              <Typography variant="caption" color="text.disabled" textAlign="center">
                {isDemoUser ? "Export is available in full access." : "Export matches your on-screen preview"}
              </Typography>
            )}
          </Box>
          )}
        </Box>

        {/* ── Main preview area ── */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: "grey.50",
            p: 3,
            overflowY: "auto",
            scrollBehavior: "smooth",
          }}
        >
          {draftItem && this.renderReportCard(draftItem, { isDraft: true })}

          {selectedConstituencies.length > 0 ? (
            selectedConstituencies.map(c => this.renderReportCard(c))
          ) : draftItem ? null : (
            <Box
              sx={{
                height: "100%",
                display: "grid",
                placeItems: "center",
                color: "text.secondary",
              }}
            >
              <Stack spacing={1} alignItems="center">
                <PictureAsPdfIcon fontSize="large" />
                <Typography variant="body1">No preview yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select one or more constituencies to render reports.
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>

      </Box>
    );
  }
}
